import {
  type ConstructionSiteSnapshot,
  type GroundItemSnapshot,
  getObjectProperties,
  toObjectType,
  type WorldObjectSnapshot,
} from '@dreamgame/shared';
import { computed, signal } from '@preact/signals';
import { getAllObjects } from './chunks';
import type { CorpseData } from './corpses';
import type { PawnData } from './entities';
import { hasSelection } from './selection';

// Context menu action definition
export interface ContextMenuAction {
  readonly id: string;
  readonly label: string;
  readonly icon?: string;
  readonly color?: 'default' | 'danger' | 'success' | 'warning';
  readonly disabled?: boolean;
  readonly disabledReasons?: ReadonlyArray<string> | undefined;
  readonly execute: () => void;
}

// Target types the context menu can interact with
export type ContextMenuTargetType =
  | 'ground'
  | 'object'
  | 'pawn'
  | 'animal'
  | 'building'
  | 'construction'
  | 'groundItem'
  | 'inventoryItem'
  | 'corpse';

export interface ContextMenuTarget {
  readonly type: ContextMenuTargetType;
  readonly tileX: number;
  readonly tileY: number;
  readonly object?: WorldObjectSnapshot;
  readonly objectName?: string;
  readonly constructionSite?: ConstructionSiteSnapshot;
  // For entity targets (pawns, animals)
  readonly entityNetworkId?: number;
  readonly entityName?: string;
  // For pawn targets (downed pawns etc.)
  readonly pawnData?: PawnData;
  // For ground items (supports multiple items at same location)
  readonly groundItems?: ReadonlyArray<GroundItemSnapshot>;
  // For inventory items
  readonly inventoryItem?: {
    readonly itemId: number;
    readonly itemName: string;
    readonly slotIndex: number;
  };
  // For corpses
  readonly corpse?: CorpseData;
  readonly corpseName?: string;
}

// Context menu state
interface ContextMenuState {
  readonly isOpen: boolean;
  readonly screenX: number;
  readonly screenY: number;
  readonly target: ContextMenuTarget | null;
  readonly actions: ReadonlyArray<ContextMenuAction>;
  readonly isShiftQueued: boolean;
}

const initialState: ContextMenuState = {
  isOpen: false,
  screenX: 0,
  screenY: 0,
  target: null,
  actions: [],
  isShiftQueued: false,
};

export const contextMenuState = signal<ContextMenuState>(initialState);

// Derived signals
export const isContextMenuOpen = computed(() => contextMenuState.value.isOpen);
export const contextMenuActions = computed(() => contextMenuState.value.actions);
export const contextMenuTarget = computed(() => contextMenuState.value.target);
export const contextMenuPosition = computed(() => ({
  x: contextMenuState.value.screenX,
  y: contextMenuState.value.screenY,
}));
export const isShiftQueued = computed(() => contextMenuState.value.isShiftQueued);

// Find object at world position
export const getObjectAtPosition = ({
  tileX,
  tileY,
}: {
  tileX: number;
  tileY: number;
}): WorldObjectSnapshot | null => {
  const allObjects = getAllObjects();

  for (const obj of allObjects) {
    const objectType = toObjectType(obj.objectType);
    const props = getObjectProperties({ objectType });
    const objMinX = obj.tileX;
    const objMaxX = obj.tileX + props.width - 1;
    const objMinY = obj.tileY;
    const objMaxY = obj.tileY + props.height - 1;

    if (tileX >= objMinX && tileX <= objMaxX && tileY >= objMinY && tileY <= objMaxY) {
      return obj;
    }
  }

  return null;
};

// Open context menu at position
export const openContextMenu = ({
  screenX,
  screenY,
  target,
  actions,
  isShiftQueued: shiftQueued,
}: {
  screenX: number;
  screenY: number;
  target: ContextMenuTarget;
  actions: ReadonlyArray<ContextMenuAction>;
  isShiftQueued?: boolean;
}): void => {
  contextMenuState.value = {
    isOpen: true,
    screenX,
    screenY,
    target,
    actions,
    isShiftQueued: shiftQueued ?? false,
  };
};

// Close context menu
export const closeContextMenu = (): void => {
  contextMenuState.value = initialState;
};

// Execute action and close menu
export const executeAction = ({ action }: { action: ContextMenuAction }): void => {
  if (!action.disabled) {
    action.execute();
  }
  closeContextMenu();
};

// Check if context menu can be opened (requires pawn selection)
export const canOpenContextMenu = (): boolean => {
  return hasSelection.value;
};
